import React, { useEffect, useMemo } from "react";
import { useUser } from "@/app/providers";
import { string, object } from "prop-types";
import { useGetGroups, useUpdateProfile } from "@/hooks";
import { SubsidiaryDropdown } from "@/components";
import type { SxProps, Theme } from "@mui/material/styles";

type SubsidiaryDropdownAutoSaveProps = {
  componentLabel?: string;
  sx?: SxProps<Theme>;
};

type SubsidiaryChangeEvent = {
  label?: string;
  value?: string;
};

/**
 * Subsidiary dropdown component
 * @param {object} props props
 * @returns {React.JSX.Element} React Component
 * @class
 */
export const SubsidiaryDropdownAutoSave = (
  props: SubsidiaryDropdownAutoSaveProps,
) => {
  const { updateProfile } = useUpdateProfile();
  const { user, setUser } = useUser();
  const { currentSubsidiaryId, currentSubsidiary, currentTeamId } = user || {};

  // const { customers: allCompanies } = useCompanies({
  //   customerId: currentTeamId,
  // });

  // const parentTeam = useMemo(() => {
  //   return allCompanies[0]?.name || "";
  // }, [allCompanies]);

  const { subsidiary } = useGetGroups({
    customerId: currentTeamId,
  });

  // Only show "All" option when there are 2 or more subsidiaries
  const showAll = useMemo(() => {
    return subsidiary?.length > 1;
  }, [subsidiary]);

  // Check if current selection is valid when subsidiaries change
  useEffect(() => {
    if (subsidiary && subsidiary.length > 0) {
      // Check if currentSubsidiaryId exists in the subsidiary list
      let foundByName;
      let foundSubsidiary;
      const foundById = subsidiary.find(
        (sub) => sub.id === currentSubsidiaryId,
      );

      foundSubsidiary = foundById;

      if (!foundById && currentSubsidiary && currentSubsidiaryId !== "all") {
        foundByName = subsidiary.find((sub) => sub.name === currentSubsidiary);
        foundSubsidiary = foundByName;
      }

      // If ID doesn't match but name does, update the ID
      if (!foundById && !foundByName && currentSubsidiaryId !== "all") {
        if (foundSubsidiary) {
          // Found subsidiary by name, update ID to match
          const variables = {
            currentSubsidiary: foundSubsidiary.name,
            currentSubsidiaryId: foundSubsidiary.id,
          };
          setUser((prev: any) => ({
            ...prev,
            ...variables,
          }));
          updateProfile(variables);
        } else {
          // Selection invalid, reset to first subsidiary or "all"
          const newSubsidiary = showAll
            ? { id: "all", name: "All" }
            : subsidiary[0];

          if (newSubsidiary) {
            const variables = {
              currentSubsidiary: newSubsidiary.name,
              currentSubsidiaryId: newSubsidiary.id,
            };

            setUser((prev: any) => ({
              ...prev,
              ...variables,
            }));
            updateProfile(variables);
          }
        }
      }
    }
  }, [
    subsidiary,
    currentSubsidiaryId,
    currentSubsidiary,
    showAll,
    setUser,
    updateProfile,
  ]);

  const handleChange = (event: SubsidiaryChangeEvent) => {
    if (
      event.value &&
      event.value !== currentSubsidiaryId &&
      event.value !== ""
    ) {
      const variables = {
        currentSubsidiary: event.value === "all" ? "All" : event.label,
        currentSubsidiaryId: event.value,
      };
      const updatedUser = {
        ...user,
        ...variables,
      };
      setUser(updatedUser);
      updateProfile(variables);
    }
  };

  return (
    <SubsidiaryDropdown
      defaultValue={currentSubsidiaryId}
      showAll={showAll}
      required
      onChange={handleChange}
      componentLabel={props.componentLabel}
      currentSubsidiary={currentSubsidiary}
      // currentTeam={currentTeam}
      currentTeamId={currentTeamId}
      cacheKey="AppBar"
      sx={{
        ...props.sx,
      }}
    />
  );
};

SubsidiaryDropdownAutoSave.propTypes = {
  componentLabel: string,
  sx: object,
};
